import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ArrowLeft, CheckCircle2, Shield, DollarSign, Users, MapPin, UserPlus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import useSEO from '../hooks/useSEO';
import { Toaster, toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_LABELS = ['Personal', 'Professional', 'About You'];

const VALUE_PROPS = [
  { icon: DollarSign, text: '90% Commission Split' },
  { icon: Shield, text: 'Zero Desk & Monthly Fees' },
  { icon: Users, text: 'Join a Brokerage of Top Producers' },
  { icon: MapPin, text: 'NY, PA & FL Markets' },
];

const stepVariants = {
  enter: { opacity: 0, x: 24 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 },
};

export default function ApplyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || '';

  useSEO({
    title: 'Apply to Join Krieger Real Estate | Agent Application',
    description: 'Apply to join Krieger Real Estate and earn up to 90% commission. Simple online application for residential and commercial agents in NY, PA, and FL.',
    keywords: 'apply real estate brokerage, join krieger, real estate agent application, switch broker',
    canonical: 'https://joinkrieger.com/apply',
  });

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referrerName, setReferrerName] = useState('');
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    license_number: '', license_state: '', experience_years: 0,
    specialization: '', preferred_location: '', about: '',
    has_active_license: '', sales_transactions_last_year: 0,
    rental_transactions_last_year: 0, lead_generation_method: '', monthly_marketing_budget: '',
  });

  useEffect(() => {
    if (refCode) {
      axios.get(`${API}/referral/validate/${refCode}`).then(res => {
        if (res.data.valid) setReferrerName(res.data.referrer_name);
      }).catch(() => {});
    }
  }, [refCode]);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (refCode) payload.referral_code = refCode;
      await axios.post(`${API}/applications`, payload);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceedStep1 = form.first_name && form.last_name && form.email && form.phone;
  const canProceedStep2 = form.preferred_location;
  const canProceedStep3 = form.has_active_license && form.lead_generation_method && form.monthly_marketing_budget;

  /* ─── Left visual panel (desktop only) ─── */
  const LeftPanel = () => (
    <div className="hidden md:flex flex-col justify-between w-5/12 lg:w-[45%] relative overflow-hidden" data-testid="apply-left-panel">
      {/* Background image */}
      <img
        src="https://images.unsplash.com/photo-1763559720203-49759c74fe82?w=1920&q=80&auto=format&fit=crop"
        alt="Manhattan skyline at night"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#001845]/90 via-[#001845]/80 to-[#002FA7]/70" />

      {/* Content */}
      <div className="relative z-10 flex flex-col justify-between h-full p-10 lg:p-14">
        {/* Top: Brand */}
        <div>
          <span className="font-['Cabinet_Grotesk'] text-2xl font-bold tracking-[0.15em] text-white">
            KRIEGER
          </span>
          <span className="text-[10px] tracking-[0.2em] uppercase text-white/50 font-semibold ml-3">
            Real Estate
          </span>
        </div>

        {/* Middle: Value prop */}
        <div className="flex-1 flex flex-col justify-center -mt-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="text-3xl lg:text-4xl font-['Cabinet_Grotesk'] font-bold text-white leading-tight mb-8"
          >
            Keep 90% of<br />
            Your Commission.<br />
            <span className="text-[#38BDF8]">Every Single Deal.</span>
          </motion.h2>

          <div className="space-y-4">
            {VALUE_PROPS.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 + i * 0.1, ease: 'easeOut' }}
                className="flex items-center gap-3"
              >
                <div className="w-9 h-9 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <item.icon size={16} className="text-[#38BDF8]" />
                </div>
                <span className="text-sm text-white/90 font-medium">{item.text}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom: Testimonial-style trust */}
        <div className="border-t border-white/10 pt-6">
          <p className="text-sm text-white/60 leading-relaxed italic">
            {`"Switching to Krieger was the best career decision I've made. The commission structure speaks for itself."`}
          </p>
          <p className="text-xs text-white/40 mt-2 font-medium">— Licensed Agent, New York</p>
        </div>
      </div>
    </div>
  );

  /* ─── Success screen ─── */
  if (submitted) {
    return (
      <div className="min-h-screen flex" data-testid="apply-success">
        <Toaster position="top-right" />
        <LeftPanel />
        <div className="flex-1 flex items-center justify-center p-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="text-center max-w-md"
          >
            <div className="w-16 h-16 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={28} className="text-emerald-600" />
            </div>
            <h1 className="text-3xl font-['Cabinet_Grotesk'] font-bold mb-3 text-slate-900">
              Application Received
            </h1>
            <p className="text-slate-500 mb-8 leading-relaxed">
              {`Thank you, ${form.first_name}! We've received your application and our team will review it within 24-48 hours. You'll receive an email with next steps once approved.`}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => navigate('/application-status')}
                className="bg-[#002FA7] text-white hover:bg-[#002075] rounded-lg px-8 py-2.5 font-medium"
                data-testid="track-status-btn"
              >
                Track Your Application
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="rounded-lg px-8 py-2.5 font-medium"
                data-testid="back-home-btn"
              >
                Back to Home
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  /* ─── Main layout ─── */
  return (
    <div className="min-h-screen flex bg-[#FAFBFC]" data-testid="apply-page">
      <Toaster position="top-right" />

      {/* Left visual panel */}
      <LeftPanel />

      {/* Right form panel */}
      <div className="flex-1 flex flex-col min-h-screen overflow-y-auto">
        {/* Top bar */}
        <div className="px-6 md:px-10 lg:px-14 pt-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-700 transition-colors group"
            data-testid="back-link"
          >
            <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
            Back to Home
          </button>
        </div>

        {/* Form container — vertically centered */}
        <div className="flex-1 flex items-center px-6 md:px-10 lg:px-14 py-10">
          <div className="w-full max-w-lg">
            {/* Mobile-only brand badge */}
            <div className="md:hidden mb-6">
              <span className="text-xs font-semibold uppercase tracking-widest text-[#002FA7] bg-[#EFF3FF] px-3 py-1 rounded-full">
                90% Commission — Zero Fees
              </span>
            </div>

            <span className="text-xs tracking-[0.25em] uppercase text-[#002FA7] font-semibold block mb-3">
              Agent Application
            </span>

            {referrerName && (
              <div className="mb-4 flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-lg" data-testid="referral-banner">
                <UserPlus size={14} className="text-emerald-600 shrink-0" />
                <span className="text-xs text-emerald-700">Referred by <strong>{referrerName}</strong></span>
              </div>
            )}

            <h1 className="text-3xl sm:text-4xl font-['Cabinet_Grotesk'] font-bold tracking-tight text-slate-900 mb-2">
              Join Krieger
            </h1>
            <p className="text-base text-slate-500 mb-8">
              Complete the steps below to start your journey.
            </p>

            {/* ─── Stepper ─── */}
            <div className="flex items-center gap-1 mb-10" data-testid="apply-stepper">
              {STEP_LABELS.map((label, i) => {
                const s = i + 1;
                const isActive = step === s;
                const isDone = step > s;
                return (
                  <React.Fragment key={s}>
                    <div className="flex items-center gap-2">
                      <div
                        className={`
                          w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300
                          ${isDone ? 'bg-[#002FA7] text-white' : ''}
                          ${isActive ? 'bg-[#002FA7] text-white ring-4 ring-[#002FA7]/15' : ''}
                          ${!isActive && !isDone ? 'bg-slate-100 text-slate-400 border border-slate-200' : ''}
                        `}
                      >
                        {isDone ? <CheckCircle2 size={14} /> : s}
                      </div>
                      <span className={`text-xs font-medium hidden sm:inline transition-colors ${isActive ? 'text-slate-900' : 'text-slate-400'}`}>
                        {label}
                      </span>
                    </div>
                    {s < 3 && (
                      <div className={`flex-1 h-px mx-2 transition-colors duration-300 ${step > s ? 'bg-[#002FA7]' : 'bg-slate-200'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* ─── Form Steps ─── */}
            <AnimatePresence mode="wait">
              {/* Step 1: Personal */}
              {step === 1 && (
                <motion.div
                  key="step1"
                  variants={stepVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                  className="space-y-5"
                >
                  <h2 className="text-lg font-['Cabinet_Grotesk'] font-semibold text-slate-900 mb-1">Personal Information</h2>
                  <p className="text-sm text-slate-400 mb-4">Tell us who you are.</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1.5 block">First Name *</Label>
                      <Input
                        value={form.first_name}
                        onChange={(e) => update('first_name', e.target.value)}
                        className="bg-white border-slate-200 text-slate-900 rounded-lg focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] transition-all"
                        placeholder="John"
                        data-testid="input-first-name"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Last Name *</Label>
                      <Input
                        value={form.last_name}
                        onChange={(e) => update('last_name', e.target.value)}
                        className="bg-white border-slate-200 text-slate-900 rounded-lg focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] transition-all"
                        placeholder="Doe"
                        data-testid="input-last-name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Email *</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => update('email', e.target.value)}
                      className="bg-white border-slate-200 text-slate-900 rounded-lg focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] transition-all"
                      placeholder="john@example.com"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Phone *</Label>
                    <Input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => update('phone', e.target.value)}
                      className="bg-white border-slate-200 text-slate-900 rounded-lg focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] transition-all"
                      placeholder="(555) 123-4567"
                      data-testid="input-phone"
                    />
                  </div>

                  <div className="pt-2">
                    <Button
                      onClick={() => setStep(2)}
                      disabled={!canProceedStep1}
                      className="bg-[#002FA7] text-white hover:bg-[#002075] rounded-lg px-6 py-2.5 font-medium disabled:opacity-40 transition-all"
                      data-testid="step1-next-btn"
                    >
                      Continue <ArrowRight size={14} className="ml-1.5" />
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Professional */}
              {step === 2 && (
                <motion.div
                  key="step2"
                  variants={stepVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                  className="space-y-5"
                >
                  <h2 className="text-lg font-['Cabinet_Grotesk'] font-semibold text-slate-900 mb-1">Professional Details</h2>
                  <p className="text-sm text-slate-400 mb-4">Help us understand your experience.</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1.5 block">License Number</Label>
                      <Input
                        value={form.license_number}
                        onChange={(e) => update('license_number', e.target.value)}
                        className="bg-white border-slate-200 text-slate-900 rounded-lg"
                        placeholder="Optional"
                        data-testid="input-license-number"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1.5 block">License State</Label>
                      <Select value={form.license_state || undefined} onValueChange={(v) => update('license_state', v)}>
                        <SelectTrigger className="bg-white border-slate-200 text-slate-900 rounded-lg" data-testid="select-license-state">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200">
                          <SelectItem value="NY">New York</SelectItem>
                          <SelectItem value="PA">Pennsylvania</SelectItem>
                          <SelectItem value="FL">Florida</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Years of Experience</Label>
                      <Input
                        type="number"
                        value={form.experience_years}
                        onChange={(e) => update('experience_years', parseInt(e.target.value) || 0)}
                        className="bg-white border-slate-200 text-slate-900 rounded-lg"
                        data-testid="input-experience"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Specialization</Label>
                      <Select value={form.specialization || undefined} onValueChange={(v) => update('specialization', v)}>
                        <SelectTrigger className="bg-white border-slate-200 text-slate-900 rounded-lg" data-testid="select-specialization">
                          <SelectValue placeholder="Select focus" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200">
                          <SelectItem value="multifamily">Multifamily</SelectItem>
                          <SelectItem value="office">Office Buildings</SelectItem>
                          <SelectItem value="retail_restaurant">Retail / Restaurant</SelectItem>
                          <SelectItem value="self_storage">Self Storage</SelectItem>
                          <SelectItem value="warehouse_industrial">Warehouse / Industrial</SelectItem>
                          <SelectItem value="mixed_commercial">Mixed Commercial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Preferred Location *</Label>
                    <Select value={form.preferred_location || undefined} onValueChange={(v) => update('preferred_location', v)}>
                      <SelectTrigger className="bg-white border-slate-200 text-slate-900 rounded-lg" data-testid="select-preferred-location">
                        <SelectValue placeholder="Where will you operate?" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="New York">New York</SelectItem>
                        <SelectItem value="Pennsylvania">Pennsylvania</SelectItem>
                        <SelectItem value="Florida">Florida</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setStep(1)}
                      className="border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg"
                      data-testid="step2-back-btn"
                    >
                      <ArrowLeft size={14} className="mr-1.5" /> Back
                    </Button>
                    <Button
                      onClick={() => setStep(3)}
                      disabled={!canProceedStep2}
                      className="bg-[#002FA7] text-white hover:bg-[#002075] rounded-lg px-6 py-2.5 font-medium disabled:opacity-40 transition-all"
                      data-testid="step2-next-btn"
                    >
                      Continue <ArrowRight size={14} className="ml-1.5" />
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Qualifying Questions */}
              {step === 3 && (
                <motion.div
                  key="step3"
                  variants={stepVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                  className="space-y-5"
                >
                  <h2 className="text-lg font-['Cabinet_Grotesk'] font-semibold text-slate-900 mb-1">Qualifying Questions</h2>
                  <p className="text-sm text-slate-400 mb-4">Help us understand your current business.</p>

                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Do you have an active real estate license? *</Label>
                    <Select value={form.has_active_license || undefined} onValueChange={(v) => update('has_active_license', v)}>
                      <SelectTrigger className="bg-white border-slate-200 text-slate-900 rounded-lg" data-testid="select-active-license">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="pending">Pending / In Progress</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Sales transactions in the last year?</Label>
                      <Input
                        type="number"
                        min="0"
                        value={form.sales_transactions_last_year}
                        onChange={(e) => update('sales_transactions_last_year', parseInt(e.target.value) || 0)}
                        className="bg-white border-slate-200 text-slate-900 rounded-lg"
                        data-testid="input-sales-transactions"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Rental transactions in the last year?</Label>
                      <Input
                        type="number"
                        min="0"
                        value={form.rental_transactions_last_year}
                        onChange={(e) => update('rental_transactions_last_year', parseInt(e.target.value) || 0)}
                        className="bg-white border-slate-200 text-slate-900 rounded-lg"
                        data-testid="input-rental-transactions"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">What is your primary method of lead generation? *</Label>
                    <Select value={form.lead_generation_method || undefined} onValueChange={(v) => update('lead_generation_method', v)}>
                      <SelectTrigger className="bg-white border-slate-200 text-slate-900 rounded-lg" data-testid="select-lead-gen">
                        <SelectValue placeholder="Select method" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="referrals">Referrals & Repeat Clients</SelectItem>
                        <SelectItem value="cold_calling">Cold Calling / Prospecting</SelectItem>
                        <SelectItem value="online_marketing">Online Marketing / Social Media</SelectItem>
                        <SelectItem value="networking">Networking / Industry Events</SelectItem>
                        <SelectItem value="direct_mail">Direct Mail / Canvassing</SelectItem>
                        <SelectItem value="brokerage_leads">Brokerage-Provided Leads</SelectItem>
                        <SelectItem value="multiple">Multiple Methods</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">What is your monthly budget for marketing? *</Label>
                    <Select value={form.monthly_marketing_budget || undefined} onValueChange={(v) => update('monthly_marketing_budget', v)}>
                      <SelectTrigger className="bg-white border-slate-200 text-slate-900 rounded-lg" data-testid="select-marketing-budget">
                        <SelectValue placeholder="Select budget range" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="0">{"$0 — I don't spend on marketing"}</SelectItem>
                        <SelectItem value="1-500">$1 – $500/month</SelectItem>
                        <SelectItem value="500-1000">$500 – $1,000/month</SelectItem>
                        <SelectItem value="1000-2500">$1,000 – $2,500/month</SelectItem>
                        <SelectItem value="2500-5000">$2,500 – $5,000/month</SelectItem>
                        <SelectItem value="5000+">$5,000+/month</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setStep(2)}
                      className="border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg"
                      data-testid="step3-back-btn"
                    >
                      <ArrowLeft size={14} className="mr-1.5" /> Back
                    </Button>
                    <Button
                      onClick={() => setStep(4)}
                      disabled={!canProceedStep3}
                      className="bg-[#002FA7] text-white hover:bg-[#002075] rounded-lg px-6 py-2.5 font-medium disabled:opacity-40 transition-all"
                      data-testid="step3-next-btn"
                    >
                      Continue <ArrowRight size={14} className="ml-1.5" />
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Step 4: About + Summary */}
              {step === 4 && (
                <motion.div
                  key="step4"
                  variants={stepVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                  className="space-y-5"
                >
                  <h2 className="text-lg font-['Cabinet_Grotesk'] font-semibold text-slate-900 mb-1">Tell Us About Yourself</h2>
                  <p className="text-sm text-slate-400 mb-4">Almost there — just a few more details.</p>

                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">About You</Label>
                    <Textarea
                      value={form.about}
                      onChange={(e) => update('about', e.target.value)}
                      className="bg-white border-slate-200 text-slate-900 rounded-lg min-h-[120px] focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] transition-all"
                      placeholder="Tell us about your experience, goals, and why you'd like to join Krieger Real Estate..."
                      data-testid="input-about"
                    />
                  </div>

                  {/* Summary card */}
                  <div className="border border-slate-200 rounded-xl p-5 bg-slate-50/50" data-testid="application-summary">
                    <h3 className="text-sm font-semibold text-slate-800 mb-3 font-['Cabinet_Grotesk']">Application Summary</h3>
                    <div className="grid grid-cols-2 gap-y-2.5 gap-x-4 text-sm">
                      <div><span className="text-slate-400">Name</span><p className="text-slate-800 font-medium">{form.first_name} {form.last_name}</p></div>
                      <div><span className="text-slate-400">Email</span><p className="text-slate-800 font-medium">{form.email}</p></div>
                      <div><span className="text-slate-400">Phone</span><p className="text-slate-800 font-medium">{form.phone}</p></div>
                      <div><span className="text-slate-400">Location</span><p className="text-slate-800 font-medium">{form.preferred_location || '—'}</p></div>
                      {form.license_state && (
                        <div><span className="text-slate-400">License</span><p className="text-slate-800 font-medium">{form.license_state}</p></div>
                      )}
                      {form.specialization && (
                        <div><span className="text-slate-400">Focus</span><p className="text-slate-800 font-medium">{form.specialization.replace(/_/g, ' ')}</p></div>
                      )}
                      <div><span className="text-slate-400">Active License</span><p className="text-slate-800 font-medium capitalize">{form.has_active_license || '—'}</p></div>
                      <div><span className="text-slate-400">Sales (Last Yr)</span><p className="text-slate-800 font-medium">{form.sales_transactions_last_year}</p></div>
                      <div><span className="text-slate-400">Rentals (Last Yr)</span><p className="text-slate-800 font-medium">{form.rental_transactions_last_year}</p></div>
                      <div><span className="text-slate-400">Lead Gen</span><p className="text-slate-800 font-medium capitalize">{form.lead_generation_method?.replace(/_/g, ' ') || '—'}</p></div>
                      <div><span className="text-slate-400">Marketing Budget</span><p className="text-slate-800 font-medium">{form.monthly_marketing_budget || '—'}</p></div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setStep(3)}
                      className="border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg"
                      data-testid="step4-back-btn"
                    >
                      <ArrowLeft size={14} className="mr-1.5" /> Back
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="bg-[#002FA7] text-white hover:bg-[#002075] rounded-lg px-8 py-2.5 font-medium transition-all"
                      data-testid="submit-application-btn"
                    >
                      {submitting ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Submitting...
                        </span>
                      ) : (
                        'Submit Application'
                      )}
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Footer micro-text */}
        <div className="px-6 md:px-10 lg:px-14 pb-6">
          <p className="text-xs text-slate-300">
            By submitting, you agree to our terms and privacy policy.
          </p>
        </div>
      </div>
    </div>
  );
}
